import {
  getDeleteApiLabInstitutionIdLabIdMutationOptions,
  type LabViewDTO,
} from '@smartsash/api-client';
import { useEntityDelete } from '@smartsash/server-ops';
import { Icon, openModal } from '@smartsash/ui';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { certificationsFilteredByLab } from '@/ui/pages/Certifications/certificationsModel';
import { DEVICES_LAB_FIELD, devicesFilteredBy } from '@/ui/pages/Devices/devicesModel';
import { usersFilteredByLab } from '@/ui/pages/Users/usersModel';
import { RowActions as Shared, type RowActionEntry } from '@/ui/shared/RowActions';

function RowActions({ row }: { row: LabViewDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // HS-869: the card/menu read the DTO the list query returns.
  const id = row.id ?? '';
  // HS-847: delete via server-ops so the lab list query is invalidated (list
  // refreshes in place) and a toast is emitted through the shared sink. Lab
  // delete is keyed by the composite { institutionId, labId } per the generated contract.
  const del = useEntityDelete({
    entity: 'lab',
    mutationOptions: getDeleteApiLabInstitutionIdLabIdMutationOptions,
    // HS-1141: the deleted record's own reads are dropped, not refetched into a 404.
    deletesRecord: true,
  });

  // HS-1177: the imperative modal replaces a `useState` pair plus a conditionally
  // rendered dialog. It resolves with the pressed action's `value`, or `null` when
  // dismissed via the overlay, Escape or the close button.
  const handleDelete = useCallback(async () => {
    const { action } = await openModal({
      title: t('laboratories.deleteTitle', { name: row.name }),
      description: t('laboratories.deleteDescription'),
      variant: 'error',
      icon: <Icon name="trash-01" />,
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        { label: t('common.delete'), value: 'confirm', destructive: true },
      ],
    }).closed;

    if (action === 'confirm') del.mutate({ institutionId: row.institutionId ?? '', labId: id });
  }, [del, id, row.institutionId, row.name, t]);

  const items: RowActionEntry[] = [
    {
      label: t('common.edit'),
      permission: 'laboratory.edit',
      icon: 'edit-01',
      onClick: () => {
        void navigate({ to: '/laboratories/$id/edit', params: { id } });
      },
    },
    null,
    // HS-1376: a laboratory belongs to exactly one group, so this is a direct detail-page link —
    // mirrors `Devices/RowActions.tsx`'s "View laboratory" placement, right after Edit.
    {
      label: t('laboratories.actions.viewGroup'),
      icon: 'dataflow-03',
      onClick: () => {
        void navigate({ to: '/groups/$id', params: { id: row.groupId ?? '' } });
      },
    },
    null,
    {
      label: t('laboratories.actions.addDevice'),
      permission: 'device.create',
      icon: 'plus',
      // The device form narrows its laboratory select by institution, so the institution has to
      // travel with the laboratory or the preselected lab is set but invisible in the list.
      onClick: () => {
        void navigate({
          to: '/devices/new',
          search: { institutionId: row.institutionId, labId: row.id },
        });
      },
    },
    null,
    {
      label: t('laboratories.actions.viewCertifications'),
      icon: 'award-01',
      // HS-1511: this opened the unfiltered list, so every laboratory's shortcut showed every
      // programme in the system. HS-1162 already had `labId` as a live parameter and deferred
      // narrowing to its own slice — this is that slice. See `certificationsFilteredByLab`.
      onClick: () => {
        void navigate({ to: '/certifications', search: certificationsFilteredByLab(id) });
      },
    },
    {
      label: t('laboratories.actions.viewDevices'),
      icon: 'tv-01',
      onClick: () => {
        void navigate({ to: '/devices', search: devicesFilteredBy(DEVICES_LAB_FIELD, id) });
      },
    },
    {
      label: t('laboratories.actions.viewUsers'),
      icon: 'users-02',
      // A real server filter: Users reads `GET /api/User/list`, whose `labId` is a GUID.
      onClick: () => {
        // HS-1240/HS-1290: the list reads `GET /api/User/list`, whose `labId` is a GUID matched
        // against the user's active assignments — so this carries the laboratory for real. The
        // old comment here still described `GET /api/User`, where `labId` was typed `number` and
        // no real id could be passed; that route is no longer what the page queries.
        void navigate({ to: '/users', search: usersFilteredByLab(id) });
      },
    },
    null,
    {
      label: t('common.delete'),
      permission: 'laboratory.delete',
      icon: 'trash-01',
      onClick: () => {
        void handleDelete();
      },
    },
  ];

  return <Shared items={items} />;
}

export default RowActions;
